import { AxiosPromise } from "axios";
import { Api, ApiUrls } from "../../helpers/api";
import { WidgetApi } from "./api/widgetApi";
import { UiFlagsStore } from "../../stores/uiFlagsStore";
import { PolicyRenewalData } from "../../helpers/policy";
import { RiskNetworkRequest } from "./risksNetworkRequestMapper";
import { AuthorizationRenewalData } from "../../helpers/renewal";

export interface WidgetApiService {
  calculate(data: any): AxiosPromise;
  propose(data: any): AxiosPromise;
  policy(data: any): AxiosPromise;
  generateClientEndsLink(proposalId: number, email: string, exceededProxies: boolean, accountNumber?: string, contactCity?: string): AxiosPromise;
  policyPdf(data: any): AxiosPromise;
  paymentFormPdf(data: any): AxiosPromise;
  pdf(fileName: string): AxiosPromise;
  risksNetwork(data: RiskNetworkRequest): AxiosPromise;
  banks(): AxiosPromise;
  storeCalculation(data: any): AxiosPromise;
  retrieveCalculation(calculationId: string): AxiosPromise;
  retrieveContractForRenewalById(contractId: string): AxiosPromise;
  retrieveContractForRenewalBySeriesAndNumber(policyRenewalData: PolicyRenewalData): AxiosPromise;
  authorizeRetrieveContractForRenewal(externalId: string, authorizationRenewalData: AuthorizationRenewalData): AxiosPromise;
  getPolicyRenewalData(authorizedPolicyRenewalOfferId: string): AxiosPromise;
  paymentMethods(): AxiosPromise;
  generateRemotePaymentLink(proposalId: number, accountNumber: string | undefined, email: string | undefined, contactCity?: string ): AxiosPromise;
  sendForApproval(id: string): AxiosPromise;
}

export class DefaultWidgetApiService implements WidgetApiService {
  constructor(private api: WidgetApi, private uiFlagsStore: UiFlagsStore) {
  }

  public calculate(data: any): AxiosPromise {
    return this.api.post(Api.getUrl(this.uiFlagsStore, ApiUrls.calculation), data);
  }

  public propose(data: any): AxiosPromise {
    return this.api.post(Api.getUrl(this.uiFlagsStore, ApiUrls.proposal), data);
  }

  public policy(data: any): AxiosPromise {
    return this.api.post(Api.getUrl(this.uiFlagsStore, ApiUrls.policy), data);
  }

  public policyPdf(data: any): AxiosPromise {
    return this.api.post(Api.getUrl(this.uiFlagsStore, ApiUrls.pdf), data,
      {
        headers: { "accept": "application/pdf" },
        responseType: "blob"
      });
  }

  public generateClientEndsLink(proposalId: number, email: string, exceededProxies: boolean, accountNumber: string, contactCity: string): AxiosPromise {
    return this.api.post(Api.getUrl(this.uiFlagsStore, ApiUrls.generateClientEndsLink), { proposalId, email, exceededProxies, accountNumber, contactCity });
  }

  public paymentFormPdf(data: any): AxiosPromise {
    return this.api.post(Api.getUrl(this.uiFlagsStore, ApiUrls.paymentFormPdf), data,
      {
        headers: { "accept": "application/pdf" },
        responseType: "blob"
      });
  }

  public pdf(fileName: string): AxiosPromise {
    const url = `${this.uiFlagsStore.sourceDomain}${fileName}`;
    return this.api.get(url, { headers: { "accept": "application/pdf" }, responseType: "blob" });
  }

  public risksNetwork(data: RiskNetworkRequest): AxiosPromise {
    return this.api.post(Api.getUrl(this.uiFlagsStore, ApiUrls.risksNetwork), data);
  }

  public banks(): AxiosPromise {
    const url = Api.getUrl(this.uiFlagsStore, ApiUrls.banks);
    return this.api.get(url);
  }

  public storeCalculation(data: any): AxiosPromise {
    return this.api.post(Api.getUrl(this.uiFlagsStore, ApiUrls.storeCalculation), data);
  }

  public retrieveCalculation(calculationId: string): AxiosPromise {
    return this.api.post(Api.getUrl(this.uiFlagsStore, ApiUrls.retrieveCalculation), { calculationId });
  }

  public retrieveContractForRenewalById(contractId: string): AxiosPromise {
    return this.api.post(Api.getUrl(this.uiFlagsStore, ApiUrls.retrieveContractForRenewalById), { contractId });
  }

  public retrieveContractForRenewalBySeriesAndNumber(policyRenewalData: PolicyRenewalData): AxiosPromise {
    return this.api.post(Api.getUrl(this.uiFlagsStore, ApiUrls.retrieveContractForRenewalBySeriesAndNumber), {
      policyNumber: policyRenewalData.number.value,
      policySeries: policyRenewalData.series,
      peselOrRegon: policyRenewalData.peselOrRegon.value
    });
  }

  public authorizeRetrieveContractForRenewal(externalId: string, authorizationRenewalData: AuthorizationRenewalData): AxiosPromise {
    return this.api.post(`${Api.getUrl(this.uiFlagsStore, ApiUrls.authorizeRetrieveContractForRenewal)}${externalId}`, {
      dateOfBirth: authorizationRenewalData.dateOfBirth,
      regon: authorizationRenewalData.regon,
      firstPartOfLocality: authorizationRenewalData.firstPartOfLocality
    });
  }

  public getPolicyRenewalData(authorizedPolicyRenewalOfferId: string): AxiosPromise {
    return this.api.get(`${Api.getUrl(this.uiFlagsStore, ApiUrls.getPolicyRenewalData)}${authorizedPolicyRenewalOfferId}`);
  }

  public paymentMethods(): AxiosPromise {
    return this.api.get(Api.getUrl(this.uiFlagsStore, ApiUrls.paymentMethods));
  }

  generateRemotePaymentLink(proposalId: number, accountNumber: string | undefined, email: string | undefined, contactCity?: string | undefined): AxiosPromise {
    return this.api.post(Api.getUrl(this.uiFlagsStore, ApiUrls.generateRemotePaymentLink), { proposalId, accountNumber, email, contactCity });
  }

  sendForApproval(id: string): AxiosPromise {
    return this.api.post(`${Api.getUrl(this.uiFlagsStore, ApiUrls.sendForApproval)}${id}`);
  }
}

export class WidgetApiServiceWithResponse implements WidgetApiService {
  public get calculated() {
    return this._calculated;
  }

  public get proposalIssued() {
    return this._proposalIssued;
  }

  public get policyIssued() {
    return this._policyIssued;
  }

  public get clientEndsLinkIssued() {
    return this._clientEndsLinkIssued;
  }

  public get risksNetworkIssued() {
    return this._risksNetworkIssued;
  }

  public get calculationRetrieved() {
    return this._calculationRetrieved;
  }

  public get contractForRenewalRetrieved() {
    return this._contractForRenewalRetrieved;
  }

  private _calculated = false;
  private _proposalIssued = false;
  private _policyIssued = false;
  private _clientEndsLinkIssued = false;
  private _risksNetworkIssued = false;
  private _calculationRetrieved = false;
  private _contractForRenewalRetrieved = false;
  private _retrieveContractForRenewalAuthorized = false;

  constructor(private response: any) {
  }

  public calculate() {
    this._calculated = true;
    return Promise.resolve(this.response);
  }

  public propose() {
    this._proposalIssued = true;
    return Promise.resolve(this.response);
  }

  public policy() {
    this._policyIssued = true;
    return Promise.resolve(this.response);
  }

  public policyPdf() {
    return Promise.resolve(this.response);
  }

  public generateClientEndsLink() {
    this._clientEndsLinkIssued = true;
    return Promise.resolve(this.response);
  }

  public paymentFormPdf() {
    return Promise.resolve(this.response);
  }

  public pdf() {
    return Promise.resolve(this.response);
  }

  public risksNetwork() {
    this._risksNetworkIssued = true;
    return Promise.resolve(this.response);
  }

  public banks() {
    return Promise.resolve(this.response);
  }

  public paymentMethods() {
    return Promise.resolve(this.response);
  }

  public storeCalculation() {
    return Promise.resolve(this.response);
  }

  public retrieveCalculation() {
    this._calculationRetrieved = true;
    return Promise.resolve(this.response);
  }

  public retrieveContractForRenewalById() {
    this._contractForRenewalRetrieved = true;
    return Promise.resolve(this.response);
  }

  public retrieveContractForRenewalBySeriesAndNumber() {
    this._contractForRenewalRetrieved = true;
    return Promise.resolve(this.response);
  }

  public authorizeRetrieveContractForRenewal() {
    this._retrieveContractForRenewalAuthorized = true;
    return Promise.resolve(this.response);
  }

  public getPolicyRenewalData() {
    return Promise.resolve(this.response);
  }

  generateRemotePaymentLink(proposalId: number, accountNumber: string, email: string | undefined, contactCity?: string | undefined): AxiosPromise {
    return Promise.resolve(this.response);
  }

  sendForApproval(id: string): AxiosPromise {
    return Promise.resolve(this.response);
  }
}

export class WidgetApiServiceWithError implements WidgetApiService {
  constructor(private error: any) {
  }

  public calculate() {
    return Promise.reject(this.error);
  }

  public propose() {
    return Promise.reject(this.error);
  }

  public policy() {
    return Promise.reject(this.error);
  }

  public policyPdf() {
    return Promise.reject(this.error);
  }

  public generateClientEndsLink() {
    return Promise.reject(this.error);
  }

  public paymentFormPdf() {
    return Promise.reject(this.error);
  }

  public pdf() {
    return Promise.reject(this.error);
  }

  public risksNetwork() {
    return Promise.reject(this.error);
  }

  public banks() {
    return Promise.reject(this.error);
  }

  public paymentMethods() {
    return Promise.reject(this.error);
  }

  public storeCalculation() {
    return Promise.reject(this.error);
  }

  public retrieveContractForRenewalById() {
    return Promise.reject(this.error);
  }

  public retrieveContractForRenewalBySeriesAndNumber() {
    return Promise.reject(this.error);
  }

  public authorizeRetrieveContractForRenewal() {
    return Promise.reject(this.error);
  }

  public retrieveCalculation() {
    return Promise.reject(this.error);
  }

  public getPolicyRenewalData() {
    return Promise.reject(this.error);
  }

  generateRemotePaymentLink(proposalId: number, accountNumber: string, email: string | undefined, contactCity?: string | undefined): AxiosPromise {
    return Promise.reject(this.error);
  }

  sendForApproval(): AxiosPromise {
    return Promise.reject(this.error);
  }
}
